import { CartAddEvent } from '@theme/events';

const locale = document.documentElement.lang || 'en';
const root = window.Shopify?.routes?.root || '/';
const money = (cents, currency = 'AED') => new Intl.NumberFormat(locale, { style: 'currency', currency }).format(cents / 100);
const replaceToken = (template, token, value) => (template || '').replaceAll(token, value);
const purchaseLimit = (variant, cartCount = 0) => {
  const limits = [variant?.max, variant?.stock].filter((value) => value != null && Number.isFinite(value));
  return limits.length ? Math.max(0, Math.min(...limits) - cartCount) : null;
};
const quantity = (input, variant, limit = variant?.max) => {
  const min = variant?.min || 1;
  const step = variant?.step || 1;
  let value = Math.max(min, Math.floor(Number(input?.value) || min));
  if (limit != null) value = Math.min(value, limit);
  return min + Math.max(0, Math.floor((value - min) / step)) * step;
};
const unitPrice = (variant, count, cartCount = 0) => {
  let price = variant.price;
  let threshold = 0;
  for (const item of variant.breaks || []) {
    if (count + cartCount >= item.quantity && item.quantity >= threshold) {
      price = item.price;
      threshold = item.quantity;
    }
  }
  return price;
};
const volumePercent = (count, tiers) => tiers.reduce((best, tier) => count >= tier.quantity ? Math.max(best, tier.percent) : best, 0);
const offer = (variant, count, cartCount, percent = 0) => {
  const gross = Math.round(unitPrice(variant, count, cartCount) * count);
  const total = gross - Math.round(gross * percent / 100);
  return { gross, total, price: total / count, percent };
};
let cartRequest;
const notifyCart = (cart) => {
  document.querySelectorAll('sharifco-delivery-progress').forEach((component) => component.update(cart));
  document.querySelectorAll('sharifco-purchase-tools').forEach((component) => component.updateCart(cart));
};
const refreshCart = (force = false) => {
  if (force && cartRequest) return cartRequest.catch(() => {}).then(() => refreshCart());
  if (cartRequest) return cartRequest;
  cartRequest = fetch(`${root}cart.js`, { headers: { Accept: 'application/json' }, cache: 'no-store' })
    .then((response) => {
      if (!response.ok) throw new Error('Cart unavailable');
      return response.json();
    })
    .then((cart) => {
      notifyCart(cart);
      return cart;
    })
    .finally(() => { cartRequest = null; });
  return cartRequest;
};

class DeliveryProgress extends HTMLElement {
  update(cart) {
    if (!cart || !Number.isFinite(cart.total_price)) return;
    if (cart.currency && cart.currency !== this.dataset.currency) {
      this.hidden = true;
      return;
    }
    this.hidden = false;
    const threshold = Number(this.dataset.threshold);
    if (!(threshold > 0)) return;
    const remaining = Math.max(0, threshold - cart.total_price);
    const progress = Math.min(100, Math.max(0, cart.total_price * 100 / threshold));
    this.querySelector('[data-delivery-message]').textContent = remaining
      ? replaceToken(this.dataset.remainingTemplate, '__AMOUNT__', money(remaining, this.dataset.currency))
      : this.dataset.reached;
    this.querySelector('[data-delivery-fill]').style.width = `${progress}%`;
    this.querySelector('[role="progressbar"]').setAttribute('aria-valuenow', String(Math.round(progress)));
    this.classList.toggle('is-qualified', remaining === 0);
  }
}

class PurchaseTools extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.section = this.closest('.sharifco-product');
    if (!this.section) return;
    this.variants = JSON.parse(this.querySelector('[data-purchase-variants]').textContent);
    this.counts = Object.fromEntries((this.dataset.cartCounts || '').split(',').filter(Boolean).map((item) => item.split(':').map(Number)));
    this.tiers = this.dataset.volumeActive === 'true' ? [{ quantity: 3, percent: 5 }, { quantity: 6, percent: 10 }] : [];
    this.eligibleCount = Number(this.dataset.eligibleCartCount) || 0;
    this.eligible = this.dataset.volumeEligible === 'true';
    this.variantInput = this.section.querySelector('[data-variant-id]');
    this.quantityInput = this.section.querySelector('[data-quantity-input]');
    const options = { signal: this.controller.signal };
    this.variantInput?.addEventListener('change', () => queueMicrotask(() => this.update()), options);
    this.quantityInput?.addEventListener('input', () => this.update(), options);
    this.quantityInput?.addEventListener('change', () => this.update(), options);
    this.section.addEventListener('sharifco:quantity-change', () => this.update(), options);
    this.update();
  }

  disconnectedCallback() { this.controller?.abort(); }

  updateCart(cart) {
    if (!Array.isArray(cart.items)) return;
    this.counts = {};
    for (const item of cart.items) this.counts[item.variant_id] = (this.counts[item.variant_id] || 0) + item.quantity;
    this.eligibleCount = cart.items.reduce((sum, item) => sum + (item.gift_card || item.selling_plan_allocation ? 0 : item.quantity), 0);
    this.update();
  }

  update() {
    const variant = this.variants.find((item) => String(item.id) === this.variantInput?.value);
    const cartCount = this.counts[variant?.id] || 0;
    const limit = purchaseLimit(variant, cartCount);
    const available = Boolean(variant?.available && !this.variantInput?.disabled);
    const canAdd = available && (limit == null || limit >= (variant.min || 1));
    this.hidden = !available;
    const count = quantity(this.quantityInput, variant, limit);
    if (this.quantityInput) {
      if (limit == null) this.quantityInput.removeAttribute('max');
      else this.quantityInput.max = limit;
      if (canAdd && limit != null && Number(this.quantityInput.value) > limit) this.quantityInput.value = count;
    }
    const percent = this.eligible ? volumePercent(count, this.tiers) : 0;
    const quote = available ? offer(variant, count, 0, percent) : { total: 0, price: 0 };
    const { price, total } = quote;
    const compare = available ? Math.max(variant.price, unitPrice(variant, count)) * count : 0;
    const choices = this.querySelector('[data-quantity-choices]');
    const list = this.querySelector('[data-quantity-choice-list]');
    if (choices && available) {
      choices.hidden = false;
      const ruleMin = variant.min || 1;
      const ruleStep = variant.step || 1;
      const normalize = (value) => ruleMin + Math.ceil(Math.max(0, value - ruleMin) / ruleStep) * ruleStep;
      const amounts = [...new Set([ruleMin, normalize(Math.max(3, ruleMin * 3)), normalize(Math.max(6, ruleMin * 6))])];
      list.replaceChildren();
      for (const amount of amounts) {
        const button = document.createElement('button');
        button.type = 'button';
        button.dataset.quantityChoice = amount;
        button.setAttribute('aria-pressed', amount === count ? 'true' : 'false');
        button.disabled = limit != null && amount > limit;
        const label = document.createElement('strong');
        label.textContent = replaceToken(this.dataset.quantityTemplate, '__COUNT__', amount);
        const value = document.createElement('span');
        const choicePercent = this.eligible ? volumePercent(amount, this.tiers) : 0;
        const choiceOffer = offer(variant, amount, 0, choicePercent);
        value.textContent = money(choiceOffer.total, this.dataset.currency);
        button.append(label, value);
        if (choicePercent > 0) {
          const original = document.createElement('s');
          original.textContent = money(choiceOffer.gross, this.dataset.currency);
          const badge = document.createElement('small');
          badge.className = 'sc-purchase-tools__discount';
          badge.textContent = replaceToken(this.dataset.discountTemplate, '__PERCENT__', choicePercent);
          button.append(original, badge);
        }
        button.addEventListener('click', () => {
          if (!this.quantityInput) return;
          this.quantityInput.value = amount;
          this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        });
        list.append(button);
      }
    }
    this.querySelector('[data-purchase-total-row]').hidden = !available;
    this.querySelector('[data-purchase-total]').textContent = money(total, this.dataset.currency);
    this.querySelector('[data-purchase-unit]').textContent = replaceToken(this.dataset.eachTemplate, '__AMOUNT__', money(price, this.dataset.currency));
    const saving = this.querySelector('[data-purchase-saving]');
    saving.hidden = !available || compare <= total;
    saving.textContent = replaceToken(this.dataset.savingTemplate, '__AMOUNT__', money(compare - total, this.dataset.currency));
    this.section.dispatchEvent(new CustomEvent('sharifco:purchase-update', { detail: { variant, count, price, total, compare, currency: this.dataset.currency, available, canAdd, counts: this.counts, eligibleCount: this.eligibleCount, eligible: this.eligible, tiers: this.tiers } }));
  }
}

class ProductBundle extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.section = this.closest('.sharifco-product');
    this.button = this.querySelector('[data-bundle-add]');
    const options = { signal: this.controller.signal };
    this.section?.addEventListener('sharifco:purchase-update', (event) => {
      this.purchase = event.detail;
      this.update();
    }, options);
    this.addEventListener('change', () => this.update(), options);
    this.button.addEventListener('click', () => this.addBundle(), options);
    this.section?.querySelector('sharifco-purchase-tools')?.update();
  }

  disconnectedCallback() { this.controller?.abort(); }

  update() {
    if (!this.purchase) return;
    this.hidden = !this.purchase.available || !this.purchase.canAdd;
    if (!this.purchase.available || !this.purchase.canAdd || !this.purchase.variant) { this.button.disabled = true; return; }
    this.items = [{ id: this.purchase.variant?.id, quantity: this.purchase.count }];
    const rows = [];
    let pendingCount = this.purchase.eligible ? this.purchase.count : 0;
    for (const row of this.querySelectorAll('[data-bundle-item]')) {
      const select = row.querySelector('[data-bundle-variant]');
      const checkbox = row.querySelector('[data-bundle-check]');
      for (const option of select.options) {
        const limit = purchaseLimit({ max: option.dataset.max === '' ? null : Number(option.dataset.max), stock: option.dataset.stock === '' ? null : Number(option.dataset.stock) }, this.purchase.counts?.[option.value] || 0);
        option.disabled = limit != null && limit < (Number(option.dataset.min) || 1);
      }
      let selected = select.selectedOptions[0];
      if (!selected || selected.disabled) {
        selected = [...select.options].find((option) => !option.disabled);
        if (selected) select.value = selected.value;
      }
      if (!this.busy || !selected) checkbox.disabled = !selected;
      row.classList.toggle('is-unavailable', !selected);
      if (!selected) { checkbox.checked = false; row.classList.remove('is-selected'); continue; }
      const amount = Number(selected.dataset.min) || 1;
      const variant = {
        price: Number(selected.dataset.price),
        breaks: JSON.parse(selected.dataset.breaks || '[]'),
      };
      const checked = checkbox.checked;
      const eligible = row.dataset.volumeEligible === 'true';
      if (checked && eligible) pendingCount += amount;
      rows.push({ row, selected, amount, variant, checked, eligible });
    }
    const percent = volumePercent(pendingCount, this.purchase.tiers);
    const base = offer(this.purchase.variant, this.purchase.count, 0, this.purchase.eligible ? percent : 0);
    this.total = base.total;
    let compare = Math.max(this.purchase.variant.price, base.gross / this.purchase.count) * this.purchase.count;
    this.querySelector('[data-bundle-base-price]').textContent = money(base.total, this.purchase.currency);
    this.querySelector('[data-bundle-base-quantity]').textContent = `× ${this.purchase.count}`;
    for (const { row, selected, amount, variant, checked, eligible } of rows) {
      const quote = offer(variant, amount, 0, eligible ? percent : 0);
      row.querySelector('[data-bundle-item-price]').textContent = money(quote.total, this.purchase.currency);
      const countLabel = row.querySelector('[data-bundle-item-quantity]');
      countLabel.hidden = amount === 1;
      countLabel.textContent = replaceToken(this.dataset.quantityTemplate, '__COUNT__', amount);
      row.classList.toggle('is-selected', checked);
      if (checked) {
        this.items.push({ id: Number(selected.value), quantity: amount });
        this.total += quote.total;
        compare += Math.max(variant.price, quote.gross / amount) * amount;
      }
    }
    this.querySelector('[data-bundle-total]').textContent = money(this.total, this.purchase.currency);
    const compareLabel = this.querySelector('[data-bundle-compare]');
    compareLabel.hidden = compare <= this.total;
    compareLabel.textContent = money(compare, this.purchase.currency);
    this.button.disabled = this.busy || !this.purchase.available || this.items.length < 2;
    if (!this.busy) this.button.textContent = this.items.length < 2 ? this.dataset.selectionLabel : `${this.dataset.addLabel} · ${money(this.total, this.purchase.currency)}`;
  }

  async addBundle() {
    if (this.busy || this.button.disabled || !this.purchase.available) return;
    if (this.section.querySelector('.sharifco-product-form')?.getAttribute('aria-busy') === 'true') return;
    const input = this.section.querySelector('[data-quantity-input]');
    if (input && !input.reportValidity()) return;
    this.busy = true;
    this.button.disabled = true;
    this.button.textContent = this.dataset.busyLabel;
    this.setAttribute('aria-busy', 'true');
    this.querySelector('[data-bundle-view-cart]').hidden = true;
    const status = this.querySelector('[data-bundle-status]');
    status.hidden = true;
    const items = this.items.map((item) => ({ ...item }));
    const lockedControls = [...this.section.querySelectorAll('[data-add-button], [data-sticky-add], [data-option-value], [data-quantity-input], [data-quantity-plus], [data-quantity-minus], [data-quantity-choice], [data-bundle-check], [data-bundle-variant]')].map((node) => ({ node, disabled: node.disabled }));
    lockedControls.forEach(({ node }) => { node.disabled = true; });
    const sections = [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')].map((item) => item.dataset.sectionId))].slice(0, 5);
    try {
      const response = await fetch(window.Theme?.routes?.cart_add_url || `${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections, sections_url: location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) {
        await refreshCart(true).catch(() => {});
        document.dispatchEvent(new CartAddEvent({}, `sc-bundle-${this.section.id}`, { didError: true, source: 'sharifco-bundle', sections: result.sections }));
        status.textContent = result.description || result.message || this.dataset.errorLabel;
        status.hidden = false;
        return;
      }
      const cart = await refreshCart(true).catch(() => null);
      document.dispatchEvent(new CartAddEvent(cart || {}, `sc-bundle-${this.section.id}`, { source: 'sharifco-bundle', productId: this.section.querySelector('product-form-component')?.dataset.productId, itemCount: items.reduce((sum, item) => sum + item.quantity, 0), sections: result.sections }));
      status.textContent = this.dataset.successLabel;
      status.hidden = false;
      this.querySelector('[data-bundle-view-cart]').hidden = false;
      if (this.dataset.cartType === 'page') location.assign(this.dataset.cartUrl);
    } catch {
      status.textContent = this.dataset.errorLabel;
      status.hidden = false;
      this.querySelector('[data-bundle-view-cart]').hidden = false;
      await refreshCart(true).catch(() => {});
    } finally {
      this.busy = false;
      this.removeAttribute('aria-busy');
      lockedControls.forEach(({ node, disabled }) => { node.disabled = disabled; });
      this.update();
    }
  }
}

class ProductComparison extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();
    const section = this.closest('.sharifco-product');
    section?.addEventListener('sharifco:purchase-update', (event) => {
      const { variant, price, currency, available } = event.detail;
      const priceLabel = this.querySelector('[data-compare-current-price]');
      priceLabel.textContent = variant ? money(available ? price : variant.price, currency) : '';
      const option = this.querySelector('[data-compare-current-option]');
      if (option) option.textContent = variant?.title || '';
      this.querySelector('[data-compare-current-availability]').textContent = available ? this.dataset.availableLabel : this.dataset.soldOutLabel;
    }, { signal: this.controller.signal });
    section?.querySelector('sharifco-purchase-tools')?.update();
  }

  disconnectedCallback() { this.controller?.abort(); }
}

if (!customElements.get('sharifco-delivery-progress')) customElements.define('sharifco-delivery-progress', DeliveryProgress);
if (!customElements.get('sharifco-purchase-tools')) customElements.define('sharifco-purchase-tools', PurchaseTools);
if (!customElements.get('sharifco-product-bundle')) customElements.define('sharifco-product-bundle', ProductBundle);
if (!customElements.get('sharifco-product-comparison')) customElements.define('sharifco-product-comparison', ProductComparison);
document.addEventListener('cart:update', (event) => {
  if (Number.isFinite(event.detail?.resource?.total_price)) notifyCart(event.detail.resource);
  else refreshCart().catch(() => {});
});
window.addEventListener('pageshow', (event) => { if (event.persisted) refreshCart().catch(() => {}); });
document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refreshCart().catch(() => {}); });
document.addEventListener('keydown', (event) => {
  if (event.key !== 'Escape') return;
  document.querySelectorAll('.sc-navigation-drawer-group details[open]').forEach((details) => { details.open = false; });
});
